import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
// xlsx-js-style is a drop-in fork of SheetJS that ALSO applies cell styles
// (alignment/wrap/borders) on write — the plain `xlsx` build ignores them.
import XLSX from "xlsx-js-style";
import moment from "moment";
import AppBar from "@material-ui/core/AppBar";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import { Grid, Button, FormControl, Select, MenuItem, CircularProgress } from "@material-ui/core";
import AddIcon from '@material-ui/icons/Add';
import ChatIcon from '@material-ui/icons/Chat';
import ChatBox from "../chatbox/ChatBox";
import GenericTable from "./GenericTable";
import DocumentsTab from "./DocumentsTab";
import DocumentRepository from "../DocumentRepository";
import { fetchPDRepositoryDocumentsAPI } from "../DocumentRepository/documentRepositoryAPI";
import MessagesDashboard from "./messages/MessagesDashboard";
import { getViewTypeParam, getViewTypeFromParam } from "./tabsAPI";
import { getTabActionsConfig } from "./actions/tabActionsConfig";
import { useStyles } from './ProjectTabsStyles';
import {
  fetchTabConfigurationAPI,
  getTabAPI,
  getTabResponseKey
} from "./tabDataAPI";
import { getTabMapper } from "./tabDataMapper";
import { useProjectDevNav } from "../ProjectDevNavContext";

// Fallback name resolution for saved column preferences (Edit Project's
// tab/column config, seeded from campaignManagementColumns). A campaign's saved
// `columnName` is matched against the data mapper's own column names in
// getVisibleColumns; where the two vocabularies disagree the column would render
// blank even though the data exists. This map bridges the known differences so a
// seed name resolves to the mapper column/field that actually carries the data.
//
// Environments differ: prod's seed largely uses the mapper's names already,
// while test/older seeds use business labels (Procedure Number, Analytical
// Report Number, Result (Approved / Rejected), COAs/CQAs (Yes / No), CMO Batch
// Number, …). Both are covered here so a save works regardless of which seed the
// environment carries.
//
// Keys are normalized tab names (lowercase; see normalizeTabName) -> a map of
// lowercased seed columnName -> target. Target is either:
//   • a string  -> reuse the mapper column with that exact NAME (keeps its
//                  field + docLink/stacked/rawValue flags), or
//   • { field } -> point at a raw row FIELD directly (used for the individual
//                  CC / Deviation values the mapper otherwise only shows merged
//                  into a combined "Status / ID" column).
//
// Seed columns with genuinely no data behind them (e.g. In-Process "Method
// Name" / "Sample ID", Equipment "Specification-1/2/3") are deliberately left
// out — no fallback can populate a field the API never sends, so they correctly
// stay blank rather than showing wrong data.
const COLUMN_NAME_FALLBACKS = {
  "in-process tests": {
    "procedure number": "Operation Number",
    "analytical report number": "AR Number",
    "result (approved / rejected)": "Result",
    "coas (yes / no)": "CQAs",
    "cqas (yes / no)": "CQAs",
  },
  "product release tests": {
    "analytical report number": "AR Number",
    "result (approved / rejected)": "Result",
    "coas (yes / no)": "CQAs",
    "cqas (yes / no)": "CQAs",
  },
  "materials & consumption": {
    "procedure number": "Operation Number",
    "cmo batch number": "CMO Lot Number",
  },
  "change controls": {
    "change control type": "CC Type",
    "initial procedure number": "Initial Document Number",
    "final procedure number": "Final Document Number",
    "change control report": "CC Closure Report",
    "cmo change control status": { field: "cmoCCStatus" },
    "cmo cc status": { field: "cmoCCStatus" },
    "cmo cc id": { field: "cmoCCID" },
    "sponsor cc status": { field: "sponsorCCStatus" },
    "sponsor cc id": { field: "sponsorCCID" },
  },
  "deviations": {
    "cmo deviation status": { field: "cmoDeviationStatus" },
    "cmo deviation category": { field: "cmoDeviationCategory" },
    "cmo deviation type": { field: "cmoDeviationType" },
    "sponsor deviation status": { field: "bmsDeviationStatus" },
    "bms deviation status": { field: "bmsDeviationStatus" },
    "sponsor deviation id": { field: "bmsDeviationID" },
    "reporting date": { field: "reportingDateValue" },
    "step": { field: "deviationStep" },
    "deviation closure report": "Final Deviation Report",
  },
};

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`project-tabpanel-${index}`}
      aria-labelledby={`project-tab-${index}`}
      {...other}
    >
      {value === index && (
        // Two fixes here.
        //
        // 1. `sx` is a MUI v5 prop and this app is on @material-ui/core (v4), so
        //    the padding and offset below were silently doing nothing. Moved to
        //    `style`, which is what the rest of this file uses. The old
        //    marginTop: "-5px" is deliberately NOT carried over — it has never
        //    actually applied, and making it live now would pull every tab's
        //    content up into the tab strip.
        //
        // 2. Typography defaults to variant body1, which renders a <p>. Tab
        //    content is block markup (the Documents tab is a <div> of sections),
        //    and a <div> inside a <p> is invalid — the browser closes the
        //    paragraph early and hoists the div out as a sibling, leaving a
        //    stray paragraph box behind and putting the content outside the
        //    element that was meant to style it. component="div" keeps the
        //    typography and makes the nesting valid.
        //
        // Old code:
        // <Box sx={{ padding: "0px 10px", marginTop: "-5px" }}>
        //   <Typography>{children}</Typography>
        // </Box>
        <Box style={{ padding: "0px 10px" }}>
          <Typography component="div">{children}</Typography>
        </Box>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired,
};

function a11yProps(index) {
  return {
    id: `project-tab-${index}`,
    "aria-controls": `project-tabpanel-${index}`,
  };
}


export default function ProjectTabs({
  data,
  stickyTabs = false, // When true, the tab strip stays pinned while the expanded view scrolls
  scrollRegionHeight = null, // Height (px) of the expanded view's scroll region, used to size the table
  stickyTopOffset = 0, // Height (px) of the pinned project row the tab strip stacks beneath
  handleRefresh,
  openedProjectTab,
  handleOpenProjectTab,
  projectColumnConfig = null, // Column configuration from project data
  featuresAccess = {}, // Feature access object for filtering actions
  showActionsColumn = false, // Whether to show actions column in tables
  onActionClick = null, // Optional callback when action is clicked
  batchId = null, // Batch ID for batch-level tabs (required)
  collabrixCampaignID: collabrixCampaignIDProp = null, // Selected campaign's collabrix ID (authoritative when provided)
  operation = 'All', // Operation filter (default: "All")
  viewType: externalViewType = null, // View type from parent (FilterBar)
  showChatBox: externalShowChatBox = null, // Chat box state from parent
  showMessages: externalShowMessages = null, // Messages state from parent
  initialTabName = null, // One-shot: auto-select this tab by name once tabs load (e.g. "Deviations")
  onInitialTabConsumed = null, // Called once initialTabName has been applied, so the parent can clear it
  onViewTypeChange: externalOnViewTypeChange = null, // View type change handler from parent
  onMessagesClick: externalOnMessagesClick = null, // Messages click handler from parent
  onChatClick: externalOnChatClick = null, // Chat click handler from parent
  onBackToTabs: externalOnBackToTabs = null, // Back to tabs handler from parent
}) {
  const classes = useStyles();
  const { openBatchGenealogy } = useProjectDevNav();
  // Use external state if provided, otherwise use internal state
  const [internalViewType, setInternalViewType] = useState("systemView");
  const [internalShowChatBox, setInternalShowChatBox] = useState(false);
  const [internalShowMessages, setInternalShowMessages] = useState(false);
  
  const viewType = externalViewType !== null ? externalViewType : internalViewType;
  const showChatBox = externalShowChatBox !== null ? externalShowChatBox : internalShowChatBox;
  const showMessages = externalShowMessages !== null ? externalShowMessages : internalShowMessages;
  
  // Store numeric view parameter (1 for overall, 2 for system)
  const [viewParam, setViewParam] = useState(1);
  const [value, setValue] = useState(openedProjectTab ? openedProjectTab : 0);
  const [staticTabs, setStaticTabs] = useState([]); // Static tabs from fetchTabConfiguration
  // Per-tab active column names from the tab configuration (campaign config).
  // Shape: { <normalizedTabName>: Set(activeColumnNames) }. A tab absent here
  // means "no column config" -> show all its columns.
  const [columnConfigByTab, setColumnConfigByTab] = useState({});
  // Per-tab display rename from the config (newTabName). Shape:
  // { <normalizedTabName>: "New Display Name" }. The original tab name still
  // drives data fetching; this only changes the label shown to the user.
  const [tabRenameByName, setTabRenameByName] = useState({});
  const [tabDataMap, setTabDataMap] = useState({}); // Store data for each tab: { tabName: { columns, rows } }
  const [tabLoadingMap, setTabLoadingMap] = useState({}); // Store loading state for each tab: { tabName: boolean }
  const [loadingTabs, setLoadingTabs] = useState(false); // Loading state for fetching tab configuration
  // Bumped when the user clicks the Documents tab, so it reloads on click like
  // every other tab does (it fetches its own data, outside fetchTabData).
  const [documentsRefreshToken, setDocumentsRefreshToken] = useState(0);
  // The Documents tab's currently loaded sections, lifted here purely so
  // "Download Report" can export that tab too (see handleDownloadReport).
  const [documentsSections, setDocumentsSections] = useState([]);
  const tabDataMapRef = useRef({}); // Ref to track current tab data for cache checking
  // Height of the (sticky) tab strip. The tab table's sticky column header is
  // offset by this so it pins directly beneath the tab strip instead of hiding
  // behind it while scrolling the expanded view.
  const appBarRef = useRef(null);
  const [appBarHeight, setAppBarHeight] = useState(48);
  const fetchTabDataRef = useRef(null); // Ref to store fetchTabData function
  const currentTabNameRef = useRef(null); // Remember the selected tab across tab-config reloads (dropdown changes)
  const hasAttemptedInitialFetch = useRef(false); // Flag to prevent repeated fetch attempts

  const allTabLabels = [
    "Materials & Consumption",
    "CPPs",
    "In-Process Tests",
    "Information Only Tests",
    "Product Release Tests",
    "CQAs",
    "Deviations",
    "Change Controls",
    "Equipment",
    "Yield",
    "Documents",
  ];

  // Known alternate spellings for tab names returned by different
  // tab-configuration sources (mirrors the nameVariants map in tabDataAPI.js's
  // getTabAPI), normalized so ordering isn't broken by casing/wording drift.
  const TAB_NAME_VARIANTS = {
    "material & consumption": "materials & consumption",
    "in process test": "in-process tests",
    "in process tests": "in-process tests",
    "product release test": "product release tests",
    "change control": "change controls",
    "cc": "change controls",
  };

  const normalizeTabName = (name) => {
    const key = (name || "").trim().toLowerCase();
    return TAB_NAME_VARIANTS[key] || key;
  };

  // Tabs removed from the UI (per PD feedback). Filtered out of the tab list
  // regardless of what the tab-configuration source returns.
  const HIDDEN_TABS = ["cycle time"];
  const isHiddenTab = (name) => HIDDEN_TABS.includes(normalizeTabName(name));

  // The Documents tab renders its own sectioned layout (see ./DocumentsTab) and
  // its own data fetch, so it bypasses the generic tab-data pipeline: there is
  // no /fetchDocuments endpoint for tabDataAPI's dynamic endpoint naming to
  // call, no mapper, and its data is not { columns, rows } shaped.
  const DOCUMENTS_TAB = "documents";
  const isDocumentsTab = (name) => normalizeTabName(name) === DOCUMENTS_TAB;

  // Display label overrides — the internal tab name (used for data fetching)
  // stays the original, but the user sees the renamed label. Priority:
  // config newTabName -> the built-in "Change Controls" -> "CC" alias -> name.
  const getTabDisplayLabel = (name) => {
    const renamed = tabRenameByName[normalizeTabName(name)];
    if (renamed && String(renamed).trim()) return renamed;
    return normalizeTabName(name) === "change controls" ? "CC" : name;
  };

  // Fixed display order for tabs. "In-Process Tests" must be first so it
  // is also the default-selected tab (see the useEffect below that picks
  // staticTabs[0]).
  const TAB_DISPLAY_ORDER = [
    "In-Process Tests",
    "Product Release Tests",
    "Materials & Consumption",
    "Deviations",
    "Change Controls",
    "Equipment",
    "Yield",
    "Documents",
  ].map(normalizeTabName);

  // Get collabrixCampaignID and collabrixBatchID from data - extract as separate values
  // Simplified extraction: use batchId directly, find campaign ID with multiple fallbacks
  const collabrixCampaignID = useMemo(() => {
    // When the parent resolves the selected campaign explicitly, it is
    // authoritative — the campaign dropdown must drive the payload directly
    // (don't infer it from the batch, which breaks for campaigns/stages that
    // have no batches).
    if (collabrixCampaignIDProp != null && collabrixCampaignIDProp !== '') {
      return collabrixCampaignIDProp;
    }

    // Debug log removed — it printed the full data object (fullData: data) to the console.
    // console.log('[ProjectTabs] Extracting collabrixCampaignID', {
    //   hasData: !!data,
    //   hasProjectDetails: !!data?.projectDetails,
    //   hasCampaigns: !!data?.projectDetails?.campaigns,
    //   campaignsCount: data?.projectDetails?.campaigns?.length,
    //   batchId,
    //   fullData: data
    // });

    // If no data, return null
    if (!data) {
      console.warn('[ProjectTabs] No data provided for collabrixCampaignID extraction');
      return null;
    }

    // Try to find from projectDetails.campaigns structure
    if (data.projectDetails?.campaigns && Array.isArray(data.projectDetails.campaigns) && data.projectDetails.campaigns.length > 0) {
      // If batchId is provided, try to find the campaign that contains it
      if (batchId) {
        for (const campaign of data.projectDetails.campaigns) {
          for (const stage of campaign.stages || []) {
            for (const batch of stage.batches || []) {
              if (batch.batchId === batchId || batch.batchID === batchId || 
                  batch.batchId?.toString() === batchId?.toString() || 
                  batch.batchID?.toString() === batchId?.toString()) {
                const foundID = campaign.collabrixCampaignID || campaign.campaignID || campaign.campaignId;
                // console.log('[ProjectTabs] Found matching batch, returning campaignID:', foundID);
                return foundID;
              }
            }
          }
        }
      }
      
      // Fallback: use first campaign (always available if campaigns exist)
      const firstCampaign = data.projectDetails.campaigns[0];
      const fallbackID = firstCampaign?.collabrixCampaignID || firstCampaign?.campaignID || firstCampaign?.campaignId;
      // console.log('[ProjectTabs] Using first campaign as fallback:', fallbackID);
      return fallbackID;
    }
    
    // Additional fallback: check if data has campaignId directly
    if (data.campaignId) {
      // console.log('[ProjectTabs] Using data.campaignId:', data.campaignId);
      return data.campaignId;
    }

    // Last fallback: check campaignID
    if (data.campaignID) {
      // console.log('[ProjectTabs] Using data.campaignID:', data.campaignID);
      return data.campaignID;
    }
    
    console.warn('[ProjectTabs] Could not extract collabrixCampaignID, returning null');
    return null;
  }, [data, batchId, collabrixCampaignIDProp]);

  const collabrixBatchID = useMemo(() => {
    // console.log('[ProjectTabs] Extracting collabrixBatchID', {
    //   hasData: !!data,
    //   batchId
    // });

    // Parent-driven mode: the campaign is resolved explicitly and batchId is
    // already scoped to that campaign (or null when it has no batches). Use it
    // as-is — do NOT fall back to the first campaign's batch, which would show
    // the wrong campaign's data.
    if (collabrixCampaignIDProp != null && collabrixCampaignIDProp !== '') {
      return batchId || null;
    }

    // If batchId is provided as prop, use it directly (simplest approach)
    if (batchId) {
      // Try to find collabrixBatchID from data structure first
      if (data?.projectDetails?.campaigns && Array.isArray(data.projectDetails.campaigns)) {
        for (const campaign of data.projectDetails.campaigns) {
          for (const stage of campaign.stages || []) {
            for (const batch of stage.batches || []) {
              if (batch.batchId === batchId || batch.batchID === batchId || 
                  batch.batchId?.toString() === batchId?.toString() || 
                  batch.batchID?.toString() === batchId?.toString()) {
                const foundID = batch.collabrixBatchID || batch.batchId || batch.batchID;
                // console.log('[ProjectTabs] Found matching batch in data, returning:', foundID);
                return foundID;
              }
            }
          }
        }
      }
      
      // Use batchId directly as it's provided as prop
      // console.log('[ProjectTabs] Using batchId prop directly as collabrixBatchID:', batchId);
      return batchId;
    }
    
    // If no batchId prop, try to extract from data
    if (data?.projectDetails?.campaigns && Array.isArray(data.projectDetails.campaigns)) {
      const firstCampaign = data.projectDetails.campaigns[0];
      const firstStage = firstCampaign?.stages?.[0];
      const firstBatch = firstStage?.batches?.[0];
      if (firstBatch) {
        const fallbackID = firstBatch.collabrixBatchID || firstBatch.batchId || firstBatch.batchID;
        // console.log('[ProjectTabs] Using first batch from data:', fallbackID);
        return fallbackID;
      }
    }
    
    console.warn('[ProjectTabs] No batchId provided and could not extract from data, returning null');
    return null;
  }, [data, batchId, collabrixCampaignIDProp]);

  // collabrixCampaignStageID for the selected batch — required by the Change
  // Controls tab APIs (fetchChangeControls / fetchPlannedChange). Find the stage
  // that owns the selected batch (within the resolved campaign when provided).
  const collabrixCampaignStageID = useMemo(() => {
    const campaigns = data?.projectDetails?.campaigns;
    if (!batchId || !Array.isArray(campaigns)) return null;

    const matchesBatch = (batch) =>
      batch.collabrixBatchID === batchId ||
      batch.batchId === batchId ||
      batch.batchID === batchId ||
      String(batch.collabrixBatchID) === String(batchId) ||
      String(batch.batchId) === String(batchId) ||
      String(batch.batchID) === String(batchId);

    for (const campaign of campaigns) {
      // In parent-driven mode restrict the search to the selected campaign.
      if (
        collabrixCampaignIDProp != null &&
        collabrixCampaignIDProp !== '' &&
        String(campaign.collabrixCampaignID) !== String(collabrixCampaignIDProp)
      ) {
        continue;
      }
      for (const stage of campaign.stages || []) {
        if ((stage.batches || []).some(matchesBatch)) {
          return stage.collabrixCampaignStageID ?? null;
        }
      }
    }
    return null;
  }, [data, batchId, collabrixCampaignIDProp]);

  // Fetch static tab configuration when component mounts or viewType/campaignID changes
  // This is called when chevron is clicked (component mounts)
  useEffect(() => {
    // Clear any existing tabs first to avoid showing static data
    setStaticTabs([]);
    setTabDataMap({});
    setTabLoadingMap({});
    setColumnConfigByTab({});
    setTabRenameByName({});
    tabDataMapRef.current = {};
    
    const fetchTabConfig = async () => {
      try {
        // Use static campaign ID if not available (for testing)
        const campaignID = collabrixCampaignID || "CAMP-001";
        
        console.log('[ProjectTabs] Fetching tab configuration...', {
          viewType,
          campaignID,
          usingStatic: !collabrixCampaignID
        });
        setLoadingTabs(true);
        
        // Tab/column configuration is always read from the systemView — it is
        // the only view written, so the request no longer carries a view
        // parameter at all. numericViewParam is kept purely as the local
        // "this is System View" marker for the filtering below.
        const numericViewParam = 2;
        setViewParam(numericViewParam);

        // Fetch tab configuration - ALWAYS call API
        const response = await fetchTabConfigurationAPI(campaignID);
        
        // console.log('[ProjectTabs] Tab configuration response:', response);

        // Handle response structure: response.data.tabConfigurationList or response.data.data.tabConfigurationList
        const tabConfigurationList = response?.data?.tabConfigurationList || response?.data?.data?.tabConfigurationList;
        
        if (response?.status && tabConfigurationList && Array.isArray(tabConfigurationList) && tabConfigurationList.length > 0) {
          // Extract tab names from response — only use tabs returned by the real API.
          // Honor the campaign's on/off flag: drop any tab explicitly deactivated
          // (activated/isActive === false). Static/legacy shapes carry no flag, so
          // nothing is dropped there. Then drop hidden tabs (e.g. Cycle Time).
          // Keep only the entries for the requested view (the response carries a
          // `viewTab` per row; the dashboard asked for overallView / systemView).
          // Then keep only ACTIVE tabs (isActive !== false), and drop hidden ones.
          const wantViewTab = numericViewParam === 2 ? 'systemView' : 'overallView';
          const seenTab = new Set();
          const tabNames = tabConfigurationList
            .filter(tab => (tab?.viewTab ? tab.viewTab === wantViewTab : true))
            .filter(tab => tab?.activated !== false && tab?.isActive !== false)
            .map(tab => tab.tabName)
            .filter(name => {
              if (!name || isHiddenTab(name) || seenTab.has(name)) return false;
              seenTab.add(name);
              return true;
            });

          // ===== STATIC TABS (commented out — using only API-driven tabs) =====
          // const staticOnlyTabs = ["Equipment", "Yield"];
          // const missingStaticTabs = staticOnlyTabs.filter(name => !tabNames.includes(name));
          // const allTabs = [...tabNames, ...missingStaticTabs];

          // "Documents" is added when the campaign's tab configuration has NO
          // row for it at all. Campaigns configured before the Documents tab
          // existed carry no such row, and the tab list is otherwise strictly
          // API-driven — so without this the tab would only ever appear for
          // campaigns whose configuration happens to have been re-saved since.
          //
          // Deliberately keyed on ABSENCE, not on the activated flag: a
          // campaign that has the row and has switched Documents OFF in Edit
          // Campaign is respected (the row was filtered out above and is not
          // re-added), so this only backfills a missing row, never overrides a
          // decision the user made.
          const hasDocumentsRow = tabConfigurationList.some(
            (tab) => tab?.tabName && isDocumentsTab(tab.tabName)
          );
          if (!hasDocumentsRow) {
            tabNames.push("Documents");
          }

          // Tabs must render in this fixed order, with any unrecognized
          // tab appended at the end. The default-selection logic below
          // picks staticTabs[0], so this also makes "In-Process Tests"
          // the initially selected tab.
          const orderedTabNames = [...tabNames].sort((a, b) => {
            const aIdx = TAB_DISPLAY_ORDER.indexOf(normalizeTabName(a));
            const bIdx = TAB_DISPLAY_ORDER.indexOf(normalizeTabName(b));
            if (aIdx === -1 && bIdx === -1) return 0;
            if (aIdx === -1) return 1;
            if (bIdx === -1) return -1;
            return aIdx - bIdx;
          });

          console.log('[ProjectTabs] Setting tabs from API in fixed order:', orderedTabNames);
          setStaticTabs(orderedTabNames);

          // Tab display renames (newTabName overrides the shown label; the
          // original tabName still drives data fetching).
          const renameMap = {};
          tabConfigurationList.forEach((tab) => {
            if (tab?.viewTab && tab.viewTab !== wantViewTab) return;
            if (tab?.newTabName && String(tab.newTabName).trim() && tab?.tabName) {
              renameMap[normalizeTabName(tab.tabName)] = String(tab.newTabName).trim();
            }
          });
          setTabRenameByName(renameMap);

          // Build per-tab ACTIVE COLUMN config so the dashboard shows only the
          // columns turned on for this campaign. Tolerant of shape: each tab item
          // may carry its columns under `columns` / `columnConfiguration` /
          // `campaignColumnConfiguration`. Active flag chosen by current view
          // (system vs overall), falling back to a generic activated/selected flag.
          // Build per-tab ACTIVE COLUMN config as an ORDERED list. The config is
          // the source of truth for WHICH columns show; each entry keeps its
          // display rename (newColumnName). Active flag by current view.
          const isSystem = numericViewParam === 2 || viewType === 'systemView';
          const isColActive = (c) =>
            (isSystem ? c?.isSystemViewSelected : c?.isOverallViewSelected) ??
            c?.activated ?? c?.isActive ?? c?.selected ?? false;
          const toColEntry = (c) => ({
            columnName: String(c?.columnName || c?.name || c?.campaignColumnName || '').trim(),
            newColumnName: c?.newColumnName ? String(c.newColumnName).trim() : null,
          });

          const colCfg = {};
          tabConfigurationList.forEach((tab) => {
            // Only the requested view's entry (matches the tab filter above).
            if (tab?.viewTab && tab.viewTab !== wantViewTab) return;
            const cols = tab?.columns || tab?.columnConfiguration || tab?.campaignColumnConfiguration;
            if (!Array.isArray(cols) || cols.length === 0) return;
            const active = cols.filter(isColActive).map(toColEntry).filter((e) => e.columnName);
            if (active.length > 0) colCfg[normalizeTabName(tab.tabName)] = active;
          });

          // Fallback shape: a FLAT column list at the response root, keyed by
          // collabrixTabConfigID -> map each active column to its tab (preserving order).
          const flatCols =
            response?.data?.columnConfigurationList ||
            response?.data?.campaignColumnConfiguration ||
            response?.data?.data?.columnConfigurationList ||
            response?.data?.data?.campaignColumnConfiguration;
          if (Object.keys(colCfg).length === 0 && Array.isArray(flatCols) && flatCols.length > 0) {
            const tabIdToName = {};
            tabConfigurationList.forEach((t) => {
              const id = t?.collabrixTabConfigID ?? t?.campaignTabID;
              if (id != null && t?.tabName) tabIdToName[id] = t.tabName;
            });
            flatCols.forEach((c) => {
              const tabName = tabIdToName[c?.collabrixTabConfigID ?? c?.campaignTabID];
              if (!tabName || !isColActive(c)) return;
              const entry = toColEntry(c);
              if (!entry.columnName) return;
              const key = normalizeTabName(tabName);
              if (!colCfg[key]) colCfg[key] = [];
              colCfg[key].push(entry);
            });
          }

          setColumnConfigByTab(colCfg);

          // Note: First tab will be fetched in a separate useEffect when staticTabs changes
        } else {
          console.warn('[ProjectTabs] No tabs in response');
          setStaticTabs([]);
        }
      } catch (error) {
        console.error("[ProjectTabs] Error fetching tab configuration:", error);
        setStaticTabs([]);
      } finally {
        setLoadingTabs(false);
      }
    };

    fetchTabConfig();
  }, [viewType, collabrixCampaignID, collabrixBatchID]);

  // Get visible tabs - ONLY use API data, no fallback
  const visibleTabs = useMemo(() => {
    // Only return tabs from API - no static fallback
    return staticTabs && staticTabs.length > 0 ? staticTabs : [];
  }, [staticTabs]);

  // Build the columns to display for a tab from the CONFIG (the active columns
  // turned on for this campaign), in config order:
  //   - config column name matches a FE (mapper) column  -> keep the FE column
  //     (its field + data), applying any newColumnName rename to the header.
  //   - config column active but NOT in the FE mappers    -> show the header
  //     with an empty field (no data yet).
  // If a tab has NO column config (e.g. data not yet linked), fall back to the
  // FE mapper columns so we never render an empty table.
  const getVisibleColumns = (tabName, mapperColumns) => {
    const normTab = normalizeTabName(tabName);
    const activeCols = columnConfigByTab[normTab];
    if (!activeCols || activeCols.length === 0) return mapperColumns;

    const byName = new Map(
      (mapperColumns || []).map((c) => [String(c?.name || '').trim().toLowerCase(), c])
    );
    const byField = new Map(
      (mapperColumns || []).map((c) => [String(c?.field || ''), c])
    );
    const aliases = COLUMN_NAME_FALLBACKS[normTab] || {};

    return activeCols.map((cfg, i) => {
      const display = cfg.newColumnName || cfg.columnName;
      const cfgKey = String(cfg.columnName || '').trim().toLowerCase();

      // 1) Exact match against the mapper's own column names.
      let match = byName.get(cfgKey);

      // 2) Fallback: the seed uses a different name for a column that DOES carry
      //    data (name drift between the campaignManagementColumns seed and the
      //    data mapper). Resolve via COLUMN_NAME_FALLBACKS to either an existing
      //    mapper column (reused with its field + docLink/stacked flags) or a raw
      //    row field (e.g. the individual CC / Deviation values).
      if (!match) {
        const alias = aliases[cfgKey];
        if (typeof alias === 'string') {
          match = byName.get(alias.trim().toLowerCase());
        } else if (alias && alias.field) {
          match = byField.get(alias.field) || { field: alias.field };
        }
      }

      if (match) {
        // Show the seed's own label (renamed if configured); pull data from the
        // resolved column's field.
        return { ...match, id: match.id || `cfg-${i}-${cfg.columnName}`, name: display };
      }

      // Active in config but no data anywhere -> header only, empty cells. The
      // field intentionally doesn't exist on any row, so it renders blank.
      return { id: `cfg-${i}-${cfg.columnName}`, name: display, field: `__cfg_empty_${i}` };
    });
  };

  // Fetch tab data immediately after tabs are loaded from API.
  // Keep the user's current tab across tab-config reloads (dropdown changes);
  // fall back to the first tab on initial load or if the tab no longer exists.
  useEffect(() => {
    // Only run if we have tabs from API
    if (staticTabs.length > 0) {

      const prevTabName = currentTabNameRef.current;

      // A one-shot tab request (e.g. "Deviations" from Batch Genealogy's
      // "Open Deviation Tab") takes priority when the campaign exposes that
      // tab. Match by normalized name, then clear the request so later
      // tab-config reloads (view / campaign changes) don't re-force it.
      const requestedIndex = initialTabName
        ? visibleTabs.findIndex((t) => normalizeTabName(t) === normalizeTabName(initialTabName))
        : -1;
      if (initialTabName && onInitialTabConsumed) onInitialTabConsumed();

      const targetTabName = requestedIndex >= 0
        ? visibleTabs[requestedIndex]
        : (prevTabName && staticTabs.includes(prevTabName) ? prevTabName : staticTabs[0]);
      const targetTabIndex = visibleTabs.indexOf(targetTabName);

      // Always fetch target tab data (don't check if data exists)
      console.log('[ProjectTabs] Auto-fetching tab data:', targetTabName, {
        keptCurrentTab: targetTabName === prevTabName,
        hasCampaignID: !!collabrixCampaignID,
        hasBatchID: !!collabrixBatchID,
        willUseStatic: !collabrixCampaignID || !collabrixBatchID,
        fetchTabDataRefReady: !!fetchTabDataRef.current
      });

      // Set loading state immediately for target tab
      setTabLoadingMap(prev => ({ ...prev, [targetTabName]: true }));

      // Clear any existing data for target tab
      setTabDataMap(prev => {
        const updated = { ...prev, [targetTabName]: { columns: [], rows: [] } };
        tabDataMapRef.current = updated;
        return updated;
      });

      // Set the target tab as active
      if (targetTabIndex >= 0) {
        currentTabNameRef.current = targetTabName;
        setValue(targetTabIndex);
        if (handleOpenProjectTab) {
          handleOpenProjectTab(targetTabIndex);
        }
      }

      // Fetch data for target tab - wait for fetchTabDataRef if not ready yet
      if (fetchTabDataRef.current) {
        // Use forceRefresh=true to ensure API is called consistently
        fetchTabDataRef.current(targetTabName, true);
      } else {
        // If ref is not ready, wait a bit and try again
        const timeoutId = setTimeout(() => {
          if (fetchTabDataRef.current) {
            console.log('[ProjectTabs] fetchTabDataRef now ready, calling tab API');
            fetchTabDataRef.current(targetTabName, true);
          }
        }, 100);
        return () => clearTimeout(timeoutId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staticTabs, visibleTabs]);

  // Get the visible tab index from the original tab index
  const getVisibleTabIndex = (originalIndex) => {
    // Use visibleTabs directly since we only use API data
    if (originalIndex >= 0 && originalIndex < visibleTabs.length) {
      return originalIndex;
    }
    return 0;
  };

  // Fetch data for a specific tab
  const fetchTabData = useCallback(async (tabName, forceRefresh = false) => {
    
    const campaignID = collabrixCampaignID;
    const batchID = collabrixBatchID;

    // console.log(`[ProjectTabs] fetchTabData called for tab: ${tabName}`, {
    //   campaignID,
    //   batchID,
    //   forceRefresh,
    // });
    
    // The Documents tab owns its own fetch (DocumentsTab -> fetchPDBatchDocuments)
    // and its own loading/empty states, so the generic pipeline must not run for
    // it. Without this early return, tabNameToApiEndpoint would invent a
    // /fetchDocuments call that does not exist and the tab would settle into the
    // catch's "No data available." before DocumentsTab ever rendered.
    if (isDocumentsTab(tabName)) {
      setTabLoadingMap(prev => ({ ...prev, [tabName]: false }));
      return;
    }

    // Special handling for Batch Records and Quality Events - no API call, just show "No data" after 1 second loader
    if (tabName === "Batch Records" || tabName === "Quality Events") {
      console.log(`[ProjectTabs] Special handling for tab: ${tabName} - showing loader then "No data"`);
      
      // Set loading state for this tab
      setTabLoadingMap(prev => ({ ...prev, [tabName]: true }));
      
      // Clear old data immediately
      setTabDataMap(prev => {
        const updated = { ...prev, [tabName]: { columns: [], rows: [] } };
        tabDataMapRef.current = updated;
        return updated;
      });
      
      // Wait 1 second (loader), then show "No data"
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Set "No data" message
      const newData = { columns: [], rows: [], message: "No data available." };
      setTabDataMap(prev => {
        const updated = { ...prev, [tabName]: newData };
        tabDataMapRef.current = updated;
        return updated;
      });
      
      // Clear loading state
      setTabLoadingMap(prev => ({ ...prev, [tabName]: false }));
      return;
    }
    
    // Check if data already exists (unless force refresh) - use ref to check current state
    if (!forceRefresh && tabDataMapRef.current[tabName] && 
        tabDataMapRef.current[tabName].rows && 
        tabDataMapRef.current[tabName].rows.length > 0) {
      console.log(`[ProjectTabs] Data already exists for tab: ${tabName}, skipping fetch (use forceRefresh=true to bypass)`);
      return; // Data already loaded, skip API call
    }
    
    console.log(`[ProjectTabs] Proceeding with API call for tab: ${tabName} (forceRefresh=${forceRefresh})`);
    
    // Get API function for this tab (will be created dynamically if not in explicit map)
    const apiFunction = getTabAPI(tabName);
    if (!apiFunction) {
      console.warn(`[ProjectTabs] No API function found for tab: ${tabName}`);
      return;
    }
    
    console.log(`[ProjectTabs] API function found/created for tab: ${tabName}`);
    
    try {
      // Set loading state for this tab
      setTabLoadingMap(prev => ({ ...prev, [tabName]: true }));
      
      // Clear old data immediately to avoid showing stale data
      setTabDataMap(prev => {
        const updated = { ...prev, [tabName]: { columns: [], rows: [] } };
        tabDataMapRef.current = updated;
        return updated;
      });
      
      const payload = {
        collabrixCampaignID: campaignID,
        collabrixBatchID: batchID,
      };
      // Change Controls tab calls fetchChangeControls + fetchPlannedChange, which
      // require the selected stage id and always send collabrixBatchID = 0
      // (change controls are stage-level, not batch-level).
      if (normalizeTabName(tabName) === "change controls") {
        payload.collabrixBatchID = 0;
        payload.collabrixCampaignStageID = collabrixCampaignStageID;
      }

      // Debug logs removed — they printed the request payload and raw API
      // response (real data) to the console.
      // console.log(`[ProjectTabs] ===== CALLING API FOR TAB: ${tabName} =====`);
      // console.log(`[ProjectTabs] Payload:`, JSON.stringify(payload, null, 2));

      // Call API
      const response = await apiFunction(payload);

      // console.log(`[ProjectTabs] API response for tab: ${tabName}`, response);
      // console.log(`[ProjectTabs] Response structure:`, {
      //   hasStatus: !!response?.status,
      //   hasData: !!response?.data,
      //   hasDirectList: !!response?.cppDetailsList || !!response?.materialAndConsumptionList || !!response?.inProcessTestList,
      //   responseKeys: Object.keys(response || {})
      // });

      // Map response to table format
      const mapper = getTabMapper(tabName);
      if (mapper) {
        // Pass the full response object to mapper (it will handle different structures)
        const { columns, rows } = mapper(response);
        // console.log(`[ProjectTabs] Mapped data for tab: ${tabName}`, {
        //   columnsCount: columns.length,
        //   rowsCount: rows.length,
        //   sampleRow: rows[0]
        // });
        const newData = { columns, rows };
        setTabDataMap(prev => {
          const updated = { ...prev, [tabName]: newData };
          tabDataMapRef.current = updated; // Update ref
          return updated;
        });
      } else {
        // Fallback: try to extract data directly
        const responseKey = getTabResponseKey(tabName);
        if (responseKey && response?.data?.[responseKey]) {
          // console.log(`[ProjectTabs] Using fallback mapping for tab: ${tabName}`);
          const newData = { columns: [], rows: response.data[responseKey] };
          setTabDataMap(prev => {
            const updated = { ...prev, [tabName]: newData };
            tabDataMapRef.current = updated; // Update ref
            return updated;
          });
        } else {
          console.warn(`[ProjectTabs] No mapper and no response key found for tab: ${tabName}`);
          const newData = { columns: [], rows: [] };
          setTabDataMap(prev => {
            const updated = { ...prev, [tabName]: newData };
            tabDataMapRef.current = updated; // Update ref
            return updated;
          });
        }
      }
    } catch (error) {
      console.error(`[ProjectTabs] Error fetching data for tab ${tabName}:`, error);

      // EVERY failure is presented to the user as an empty tab, not a red
      // error. Tabs are switched on in Edit Project ahead of their backend
      // endpoints, so a user turning one on would otherwise be shown
      // "Failed to load ..." for a tab that is simply not wired up yet — and
      // the distinction between "not built", "returned 500" and "Corda
      // rejected" means nothing to them.
      //
      // Only `message` is set, never `error`: the render prefers message, and
      // leaving error unset also keeps the text in the normal grey rather than
      // the error red (see the colour on the Typography below).
      //
      // Diagnosis relies on the console.error above, which keeps the real
      // status and response. If a tab is silently empty, look there first.
      const newData = { columns: [], rows: [], message: "No data available." };

      setTabDataMap(prev => {
        const updated = { ...prev, [tabName]: newData };
        tabDataMapRef.current = updated; // Update ref
        return updated;
      });
    } finally {
      setTabLoadingMap(prev => ({ ...prev, [tabName]: false }));
    }
  }, [collabrixCampaignID, collabrixBatchID, collabrixCampaignStageID, viewType]);

  // Store fetchTabData in ref so it can be used in useEffect without causing dependency issues
  useEffect(() => {
    fetchTabDataRef.current = fetchTabData;
  }, [fetchTabData]);

  // Keep the measured tab-strip height in sync (it can wrap to two rows on
  // narrow screens), so the table's sticky header offset stays correct.
  useEffect(() => {
    if (!stickyTabs) return undefined;
    const measure = () => {
      if (appBarRef.current) {
        setAppBarHeight(appBarRef.current.offsetHeight || 48);
      }
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [stickyTabs, visibleTabs, loadingTabs, showMessages]);

  const handleChange = async (event, newValue) => {
    console.log(`[ProjectTabs] ===== TAB CLICKED =====`);
    console.log(`[ProjectTabs] Tab changed to index: ${newValue}`, {
      visibleTabs,
      newValue,
      visibleTabsLength: visibleTabs.length
    });
    
    // newValue is the index in visibleTabs (use it directly since tabs come from API)
    const tabName = visibleTabs[newValue];
    console.log(`[ProjectTabs] Selected tab name: "${tabName}"`);
    
    if (!tabName) {
      console.error(`[ProjectTabs] ERROR: No tab name found for index: ${newValue}`);
      console.error(`[ProjectTabs] visibleTabs:`, visibleTabs);
      return;
    }
    
    // Use the visibleTabs index directly since tabs are dynamic from API
    // Don't try to map to allTabLabels as it may not contain all API tabs
    currentTabNameRef.current = tabName;
    setValue(newValue);
    
    if (handleOpenProjectTab) {
      handleOpenProjectTab(newValue);
    }
    
    // Clicking a tab force-refreshes it. The Documents tab fetches its own data,
    // so bumping this token is its equivalent of the forceRefresh below —
    // without it, Documents would be the one tab that never reloads on click.
    if (isDocumentsTab(tabName)) {
      setDocumentsRefreshToken((n) => n + 1);
    }

    // Always fetch data for the selected tab when user clicks (force refresh to ensure API is called)
    console.log(`[ProjectTabs] Calling fetchTabData for tab: "${tabName}" (forceRefresh=true)`);
    try {
      await fetchTabData(tabName, true); // true = force refresh, always call API on user click
    } catch (error) {
      console.error(`[ProjectTabs] Error in handleChange for tab ${tabName}:`, error);
    }
    console.log(`[ProjectTabs] ===== TAB CLICK HANDLED =====`);
  };

  // Sync with parent component's openedProjectTab and fetch data
  useEffect(() => {
    // Only run if we have all required data
    if (openedProjectTab === undefined || openedProjectTab === null) {
      return;
    }
    
    if (!collabrixCampaignID || !collabrixBatchID || !fetchTabDataRef.current) {
      // Don't log warning repeatedly - only once
      if (!hasAttemptedInitialFetch.current) {
        console.warn("[ProjectTabs] Missing campaign or batch ID, cannot fetch tab data", {
          collabrixCampaignID,
          collabrixBatchID
        });
        hasAttemptedInitialFetch.current = true;
      }
      return;
    }
    
    // Reset flag when we have valid IDs
    hasAttemptedInitialFetch.current = false;
    
    // Check if the opened tab index is valid for visible tabs
    if (openedProjectTab >= 0 && openedProjectTab < visibleTabs.length) {
      const label = visibleTabs[openedProjectTab];
      setValue(openedProjectTab);
      // Fetch data for this tab
      fetchTabDataRef.current(label, false);
    } else if (visibleTabs.length > 0) {
      // If current tab is not valid, switch to first visible tab
      const firstVisibleTab = visibleTabs[0];
      setValue(0);
      if (handleOpenProjectTab) {
        handleOpenProjectTab(0);
      }
      // Fetch data for first visible tab
      if (firstVisibleTab) {
        fetchTabDataRef.current(firstVisibleTab, false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openedProjectTab, collabrixCampaignID, collabrixBatchID]);

  // Reset to first visible tab when view type changes and fetch data
  useEffect(() => {
    // Only run if we have all required data and tabs are available
    if (visibleTabs.length === 0 || !collabrixCampaignID || !collabrixBatchID || !fetchTabDataRef.current) {
      return;
    }
    
    const firstVisibleTab = visibleTabs[0];
    setValue(0);
    if (handleOpenProjectTab) {
      handleOpenProjectTab(0);
    }
    // Clear tab data when view type changes
    setTabDataMap({});
    tabDataMapRef.current = {}; // Clear ref
    // Fetch data for first visible tab
    fetchTabDataRef.current(firstVisibleTab, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewType, collabrixCampaignID, collabrixBatchID]);

  // Clear and refetch tab data when campaign / batch / operation changes
  useEffect(() => {
    if (visibleTabs.length === 0 || value === null || value === undefined || !fetchTabDataRef.current) {
      return;
    }
    if (value < 0 || value >= visibleTabs.length) {
      return;
    }
    const currentTabName = visibleTabs[value];

    // Campaign / batch changed → EVERY tab's cached data is now stale. Clear the
    // whole cache so opening any other tab (chevron / tab click) refetches with
    // the updated payload instead of showing the previous selection's data.
    if (!collabrixCampaignID || !collabrixBatchID) {
      // Selected campaign/stage has no batch → nothing to fetch.
      const clearedMap = { [currentTabName]: { columns: [], rows: [], message: 'No data available.' } };
      tabDataMapRef.current = clearedMap;
      setTabDataMap(clearedMap);
      return;
    }

    // Wipe all cached tabs, then refetch the current tab with the new payload.
    tabDataMapRef.current = {};
    setTabDataMap({});
    fetchTabDataRef.current(currentTabName, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchId, operation, collabrixCampaignID, collabrixBatchID]);

  const handleMessagesClick = () => {
    if (externalOnMessagesClick) {
      externalOnMessagesClick();
    } else {
      setInternalShowMessages(!showMessages);
      setInternalShowChatBox(false);
    }
  };

  const handleViewTypeChange = async (newViewType) => {
    if (externalOnViewTypeChange) {
      externalOnViewTypeChange(newViewType);
    } else {
      setInternalViewType(newViewType);
    }
    // Close messages view when switching to System/Overall View
    if (showMessages) {
      if (externalOnMessagesClick) {
        externalOnMessagesClick();
      } else {
        setInternalShowMessages(false);
      }
    }
    // Convert to numeric parameter and update
    const numericViewParam = getViewTypeParam(newViewType);
    setViewParam(numericViewParam);
    // API call will be triggered by useEffect when viewType changes
  };

  const handleChatClick = () => {
    if (externalOnChatClick) {
      externalOnChatClick();
    } else {
      setInternalShowChatBox(!showChatBox);
    }
  };

  // "Analytical Report" link (In-Process Tests / Product Release Tests tabs) —
  // opens the matching LIMS/Analytical document straight to it in Document
  // Repository, scoped by the row's AR Number + the current batch/campaign
  // (same resolution getReportContext/handleBatchGenealogy already use).
  const [analyticalReportLink, setAnalyticalReportLink] = useState(null);
  const handleAnalyticalReportClick = () => {
    const { campaign, batch } = getReportContext();
    // Scope to the batch only, no AR-number search. The report's own AR number
    // (one per LIMS report) is not the test row's per-test AR number, so
    // searching the row's AR would filter the batch's report back out. Batch
    // scoping lands the user on the batch's analytical report(s), which is the
    // FSD linkage.
    setAnalyticalReportLink({
      campaignId: campaign?.campaignId || campaign?.campaignID || collabrixCampaignID || null,
      batchNumber: batch?.batchNumber || batchId || null,
      search: '',
    });
  };

  const handleBatchGenealogy = () => {
    // Opening from the tabs view — the campaign / stage / batch filters are
    // already chosen here, so carry them as a batchContext. Batch Genealogy
    // treats this as a DIRECT entry: it opens the report straight away
    // (skipping its selection filters) and hides "Return to Main Dashboard"
    // (the user did not come from Project Analytics).
    const { campaign, stage, batch } = getReportContext();
    const batchContext = {
      projectId: data?.projectDisplayId || data?.projectId || null,
      campaignId:
        campaign?.campaignId || campaign?.campaignID || collabrixCampaignID || null,
      batchNumber: batch?.batchNumber || batchId || null,
      stage: stage?.cmoStageID || stage?.stageId || null,
    };
    // Open Batch Genealogy in place (no URL). The batchContext makes it a direct
    // entry: it opens the report straight away and hides the "Return to Project
    // Analytics" link (the user did not come from Project Analytics).
    openBatchGenealogy({ batchContext });
  };

  // Resolve the selected campaign / stage / batch (used for both the report
  // header block and the file name).
  const getReportContext = () => {
    const campaigns = data?.projectDetails?.campaigns || [];
    const campaign =
      campaigns.find(
        (c) =>
          String(c.collabrixCampaignID) === String(collabrixCampaignID) ||
          c.campaignId === collabrixCampaignID ||
          c.campaignID === collabrixCampaignID
      ) || campaigns[0] || {};

    let stage = null;
    let batch = null;
    (campaign.stages || []).some((s) => {
      const b = (s.batches || []).find(
        (bb) =>
          String(bb.batchId) === String(batchId) ||
          String(bb.collabrixBatchID) === String(batchId)
      );
      if (b) {
        stage = s;
        batch = b;
        return true;
      }
      return false;
    });
    if (!stage) stage = campaign.stages?.[0] || null;
    if (!batch) batch = stage?.batches?.[0] || null;

    return { campaign, stage, batch };
  };

  // Whether THIS batch has any Analytical document at all.
  //
  // The link is BATCH-scoped, per the FSD Analytical section: analytical
  // reports are "linked back to the corresponding Batch Number", and the
  // Analytical Report Number "is treated as a descriptive attribute only".
  // The earlier rule required the test row's AR number to EQUAL the document's
  // AR number, but a LIMS General Report carries ONE report-level number
  // (e.g. DIMCBSQ260001) while each test row shows its OWN per-test AR number
  // (DIP25082340, ...), so they essentially never matched and the link never
  // appeared even though the report was right there in the repository. The
  // link now shows on a batch's test rows whenever that batch has at least one
  // analytical document; clicking opens the Analytical repository scoped to the
  // batch so the user lands on its report(s).
  // null = not resolved yet, so no link is shown until we know.
  const [batchHasAnalyticalDoc, setBatchHasAnalyticalDoc] = useState(null);

  useEffect(() => {
    const { campaign, batch } = getReportContext();
    const campaignId =
      campaign?.campaignId || campaign?.campaignID || collabrixCampaignID || null;
    const batchNumber = batch?.batchNumber || batchId || null;
    if (!campaignId || !batchNumber) {
      setBatchHasAnalyticalDoc(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const res = await fetchPDRepositoryDocumentsAPI({
        category: 'analytical',
        campaignIds: [campaignId],
        stageId: 'all',
        batchNumber,
        search: '',
      });
      if (cancelled) return;
      // Batch linkage only — presence of any analytical document is enough.
      // This also means the link no longer depends on the AR number being
      // successfully extracted from kvpJson, only on the document existing.
      setBatchHasAnalyticalDoc((res?.data?.documents || []).length > 0);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collabrixCampaignID, batchId, data]);

  const isAnalyticalReportAvailable = () => batchHasAnalyticalDoc === true;

  // Download the CURRENT tab as an Excel file: the project/campaign/batch
  // header block on top (same fields shown in the campaign view) followed by
  // the full table of the tab (all rows, not just what's on screen). File is
  // named <productCode>_<stage>_<batchNo>_<Tab> (e.g. BSQ_A_DBSQ25001_In-Process_Tests).
  const handleDownloadReport = () => {
    const currentTabName = visibleTabs[value];
    if (!currentTabName) return;

    const { campaign, stage, batch } = getReportContext();

    const fmtDate = (v) => {
      if (!v) return '';
      const m = moment(v);
      return m.isValid() ? m.format('DD-MMM-YYYY') : v;
    };

    const stageId = stage?.cmoStageID || stage?.stageId || '';
    const chemicalNo =
      stage?.sponsorStageProductDescription || stage?.cmoStageProductDescription || '';
    const stageLabel = stageId
      ? `Stage ${stageId}${chemicalNo ? ` (${chemicalNo})` : ''}`
      : chemicalNo;

    const headerRows = [
      ['CMO', data?.cmoName || ''],
      ['Site', data?.cmoSite || ''],
      ['Project ID', data?.projectDisplayId || data?.projectId || ''],
      ['PO No.', data?.poNumber || ''],
      ['Campaign ID', campaign?.campaignId || campaign?.campaignID || ''],
      ['CMO Stage (Chemical No.)', stageLabel],
      ['CMO Batch No.', batch?.batchNumber || ''],
      ['Batch Start Date', fmtDate(batch?.batchStartDate || batch?.startDate)],
      ['Batch End Date', fmtDate(batch?.batchEndDate || batch?.endDate)],
      ['Batch Status', batch?.batchStatus || batch?.statusDescription || ''],
    ];

    // Full table for the current tab. The Documents tab keeps its data outside
    // tabDataMap (it is sectioned, not { columns, rows }), so it is flattened
    // separately — otherwise this button would export a header block above an
    // empty table for that one tab. The section and Record ID are carried onto
    // every row so the grouping survives in a flat sheet.
    let tableHeader;
    let tableRows;
    if (isDocumentsTab(currentTabName)) {
      tableHeader = [
        'Section',
        'Record ID',
        'Download Document',
        'Fields That Require Review',
        'Fields Reviewed',
        'Upload Date',
      ];
      tableRows = [];
      (documentsSections || []).forEach((section) => {
        (section.groups || []).forEach((group) => {
          (group.documents || []).forEach((doc) => {
            tableRows.push([
              section.title || '',
              group.recordId || '',
              doc.fileName || '',
              // Blank rather than 0 where the document was never digitized, so
              // the sheet says the same thing the tab does (no indicator shown).
              doc.digitized ? doc.fieldsRequiringReview : '',
              doc.digitized ? doc.fieldsReviewed : '',
              fmtDate(doc.uploadDate),
            ]);
          });
        });
      });
    } else {
      const tabData = tabDataMap[currentTabName] || { columns: [], rows: [] };
      // Export only the columns visible for this campaign (same filter as the UI).
      const cols = getVisibleColumns(currentTabName, Array.isArray(tabData.columns) ? tabData.columns : []);
      tableHeader = cols.map((c) => c.name);
      tableRows = (tabData.rows || []).map((row) =>
        cols.map((c) => {
          const v = row[c.field];
          return v == null ? '' : v;
        })
      );
    }

    const tabLabel = getTabDisplayLabel(currentTabName) || currentTabName;

    // --- Sheet layout -------------------------------------------------------
    // Row 0: report title (merged, brand banner)
    // Row 1: spacer
    // Meta block: label / value pairs (value merged across the table width)
    // spacer
    // Table header + data rows
    const aoa = [];
    aoa.push([`${tabLabel} Report`]);
    aoa.push([]);
    const metaStartRow = aoa.length;
    headerRows.forEach((hr) => aoa.push(hr));
    aoa.push([]);
    const tableHeaderRow = aoa.length;
    aoa.push(tableHeader);
    tableRows.forEach((r) => aoa.push(r));

    const worksheet = XLSX.utils.aoa_to_sheet(aoa);

    // --- Column widths ------------------------------------------------------
    // Keep columns compact: sizes come from the TABLE only (title/meta values
    // are merged, so they don't stretch the sheet). Longer content wraps onto
    // extra lines rather than widening the column. Tidy band: 10–26 chars.
    const colCount = Math.max(
      tableHeader.length,
      ...tableRows.map((r) => r.length),
      2
    );
    const colWidths = [];
    for (let c = 0; c < colCount; c += 1) {
      let maxLen = String(tableHeader[c] == null ? '' : tableHeader[c]).length;
      tableRows.forEach((row) => {
        const len = row[c] == null ? 0 : String(row[c]).length;
        if (len > maxLen) maxLen = len;
      });
      colWidths[c] = { wch: Math.min(Math.max(maxLen + 2, 10), 26) };
    }
    worksheet['!cols'] = colWidths;

    // --- Merges: banner + meta values span the full table width -------------
    const merges = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: colCount - 1 } },
    ];
    for (let i = 0; i < headerRows.length; i += 1) {
      merges.push({
        s: { r: metaStartRow + i, c: 1 },
        e: { r: metaStartRow + i, c: colCount - 1 },
      });
    }
    worksheet['!merges'] = merges;

    // --- Styling palette ----------------------------------------------------
    const BRAND = '3B2A4D';       // brand dark purple (matches the UI buttons)
    const LABEL_FILL = 'F0EEF4';  // light lavender for meta labels
    const ZEBRA_FILL = 'F7F7FA';  // subtle stripe on alternate data rows
    const thin = { style: 'thin', color: { rgb: 'D9D9D9' } };
    const allBorders = { top: thin, bottom: thin, left: thin, right: thin };

    const setStyle = (r, c, style) => {
      const ref = XLSX.utils.encode_cell({ r, c });
      const cell = worksheet[ref];
      if (cell) cell.s = { ...(cell.s || {}), ...style };
    };

    // Title banner
    setStyle(0, 0, {
      font: { bold: true, sz: 13, color: { rgb: 'FFFFFF' } },
      fill: { fgColor: { rgb: BRAND } },
      alignment: { horizontal: 'left', vertical: 'center' },
    });

    // Meta block: bold label cell + wrapped value cell
    for (let i = 0; i < headerRows.length; i += 1) {
      const r = metaStartRow + i;
      setStyle(r, 0, {
        font: { bold: true, color: { rgb: '3B2A4D' } },
        fill: { fgColor: { rgb: LABEL_FILL } },
        alignment: { vertical: 'top', wrapText: true },
        border: allBorders,
      });
      setStyle(r, 1, {
        alignment: { vertical: 'top', wrapText: true },
        border: allBorders,
      });
    }

    // Table header row: brand banner, white bold, centered
    for (let c = 0; c < colCount; c += 1) {
      setStyle(tableHeaderRow, c, {
        font: { bold: true, color: { rgb: 'FFFFFF' } },
        fill: { fgColor: { rgb: BRAND } },
        alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
        border: allBorders,
      });
    }

    // Data rows: bordered, wrapped, top-aligned, zebra striped
    for (let i = 0; i < tableRows.length; i += 1) {
      const r = tableHeaderRow + 1 + i;
      for (let c = 0; c < colCount; c += 1) {
        setStyle(r, c, {
          alignment: { vertical: 'top', wrapText: true },
          border: allBorders,
          ...(i % 2 === 1 ? { fill: { fgColor: { rgb: ZEBRA_FILL } } } : {}),
        });
      }
    }

    // Taller banner + header rows so they read as section dividers
    const rowHeights = [];
    rowHeights[0] = { hpt: 22 };
    rowHeights[tableHeaderRow] = { hpt: 20 };
    worksheet['!rows'] = rowHeights;

    // Freeze everything above the first data row so the meta block + table
    // header stay visible while scrolling long reports.
    worksheet['!freeze'] = {
      xSplit: 0,
      ySplit: tableHeaderRow + 1,
      topLeftCell: XLSX.utils.encode_cell({ r: tableHeaderRow + 1, c: 0 }),
      activePane: 'bottomLeft',
      state: 'frozen',
    };

    const workbook = XLSX.utils.book_new();
    const sheetName = String(tabLabel).replace(/[\\/?*[\]:]/g, '').slice(0, 31) || 'Report';
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

    // File name: <projectToken>_<stage>_<batchNo>_<Tab>
    const projectToken = data?.cmoProductCode || data?.productCode || 'Project';
    const fileName =
      [projectToken, stageId, batch?.batchNumber, String(tabLabel).replace(/\s+/g, '_')]
        .filter((p) => p != null && String(p).trim() !== '')
        .join('_') + '.xlsx';

    XLSX.writeFile(workbook, fileName);
  };

  // Table height for the pinned-headers layout: fill the scroll region left
  // below BOTH the pinned project row and the sticky tab strip. The 56 covers
  // the stacked margins between the tab strip and the table (the wrapping Box's
  // 10px, the table's own 10px top + 10px bottom) PLUS a bottom clearance, so
  // the table's bottom border stays inside the scroll region instead of being
  // clipped. `null` keeps the default fit-to-viewport sizing when not sticky.
  const pageScrollMaxHeight = (stickyTabs && scrollRegionHeight)
    ? Math.max(200, Math.round(scrollRegionHeight - stickyTopOffset - appBarHeight - 56))
    : null;

  return (
    <div className={classes.root}>
      <AppBar
        ref={appBarRef}
        position="static"
        className={classes.tabs}
        elevation={0}
        style={stickyTabs ? { position: 'sticky', top: stickyTopOffset, zIndex: 4 } : undefined}
      >
        <Box className={classes.tabsWithActions}>
          {!showMessages && (
            loadingTabs ? (
              // Show smooth progress bar loading for tabs
              <Box style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', padding: '0 16px', height: '48px', position: 'relative' }}>
                <Box
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    height: '2px',
                    width: '100%',
                    backgroundColor: '#e0e0e0',
                  }}
                />
                <Box
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    height: '2px',
                    width: '30%',
                    backgroundColor: '#1976d2',
                    borderRadius: '0 2px 2px 0',
                    animation: 'progressMove 1.5s ease-in-out infinite',
                  }}
                />
                <Box style={{ display: 'flex', alignItems: 'center', gap: '16px', marginLeft: 'auto', marginRight: 'auto' }}>
                  <CircularProgress size={20} thickness={4} style={{ color: '#1976d2' }} />
                  <Typography variant="body2" style={{ color: '#666', fontSize: '14px' }}>
                    Loading tabs...
                  </Typography>
                </Box>
                <style>
                  {`
                    @keyframes progressMove {
                      0% {
                        left: 0;
                        width: 30%;
                      }
                      50% {
                        left: 35%;
                        width: 30%;
                      }
                      100% {
                        left: 70%;
                        width: 30%;
                      }
                    }
                  `}
                </style>
              </Box>
            ) : (
              <Tabs
                value={value >= 0 && value < visibleTabs.length ? value : 0}
                onChange={handleChange}
                aria-label="project tabs"
                classes={{ indicator: classes.indicator, root: classes.tabsRoot }}
                variant="scrollable"
                scrollButtons="auto"
                style={{ flex: 1, minWidth: 0 }}
              >
                {visibleTabs.map((tabName, visibleIndex) => {
                  return (
                    <Tab
                      key={tabName}
                      label={getTabDisplayLabel(tabName)}
                      {...a11yProps(visibleIndex)}
                      className={classes.tab}
                    />
                  );
                })}
              </Tabs>
            )
          )}
          {/* Batch Genealogy + Download Report — shown at the right of the tab
              strip on the chevron-expanded view. Download Report exports the
              current tab (header block + full table) to Excel. */}
          {!showMessages && !loadingTabs && (
            <Box
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                flexShrink: 0,
                paddingLeft: '12px',
              }}
            >
              <Button
                variant="contained"
                disableElevation
                onClick={handleBatchGenealogy}
                style={{
                  backgroundColor: '#3B2A4D',
                  color: '#fff',
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                }}
              >
                Batch Genealogy
              </Button>
              <Button
                variant="outlined"
                onClick={handleDownloadReport}
                style={{
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                }}
              >
                Download Report
              </Button>
            </Box>
          )}
          {/* UI-PD-Changes (item 5): Chat / Messages / System View buttons
              removed from the chevron-expanded view. Disabled via `false` so
              the block and its handlers remain for easy restore. NOTE: the
              earlier FilterBar.jsx edit was dead code — THIS ProjectTabs block
              is the one actually rendered on chevron-expand. */}
          {false && !loadingTabs && (
            <Box className={classes.actionButtonsContainer} style={{ flexShrink: 0, paddingLeft: '8px', borderLeft: showMessages ? 'none' : '1px solid #e0e0e0', marginLeft: showMessages ? 'auto' : '0' }}>
            <Box 
              className={classes.chatIconButton}
              onClick={handleChatClick}
              title="Chat"
            >
              <ChatIcon />
            </Box>
            <Button
              className={classes.messagesButton}
              onClick={handleMessagesClick}
            >
              Messages
            </Button>
            <Button
              className={classes.viewButtonActive}
              onClick={() => {
                if (showMessages) {
                  // If messages is shown, close messages and keep current view
                  if (externalOnBackToTabs) {
                    externalOnBackToTabs();
                  } else {
                    setInternalShowMessages(false);
                  }
                } else {
                  // Toggle between overall view (1) and system view (2)
                  const newViewType = viewType === "systemView" ? "overallView" : "systemView";
                  handleViewTypeChange(newViewType);
                }
              }}
            >
            {/* When messages is shown, display current view. Otherwise, show target view */}
            {showMessages 
              ? (viewType === "systemView" ? "System View" : "Overall View")
              : (viewType === "systemView" ? "Overall View" : "System View")
            }
            </Button>
            </Box>
          )}
        </Box>
      </AppBar>

      <Box style={{ marginTop: '10px', display: 'flex', width: '100%', boxSizing: 'border-box', overflowX: 'hidden' }}>
        {showMessages ? (
          <Box style={{ width: '100%', flex: 1, minWidth: 0, paddingTop: '10px' }}>
            <MessagesDashboard />
          </Box>
        ) : (
          <Box style={{ flex: 1, minWidth: 0, width: '100%' }}>
            {/* Render tabs from API response */}
            {visibleTabs.map((tabName, visibleIndex) => {
              const tabData = tabDataMap[tabName] || { columns: [], rows: [] };
              const isLoading = tabLoadingMap[tabName] || false;
              const hasError = tabData.error;
              
              // Get actions configuration for this tab
              const tabActions = showActionsColumn 
                ? getTabActionsConfig(tabName, { featuresAccess, onActionClick })
                : [];
              
              // Get appropriate message based on state - simplified messages
              // Don't show "no data" while loading
              const getEmptyMessage = () => {
                if (isLoading) {
                  return null; // Don't show message while loading
                }
                // Use custom message if provided (e.g., for Batch Records and Quality Events)
                if (tabData.message) {
                  return tabData.message;
                }
                if (hasError) {
                  return `Failed to load ${tabName} data.`;
                }
                if (tabData.rows && tabData.rows.length === 0) {
                  return `No ${tabName} data available.`;
                }
                if (!tabData.rows) {
                  return `No data available.`;
                }
                return `No data available.`;
              };
              
              // The Documents tab is a sectioned layout, not a table — it takes
              // over the whole panel and manages its own fetch, loading and
              // empty states (FSD §3: numbered document-category sections, each
              // with rows grouped under a shared Record ID).
              if (isDocumentsTab(tabName)) {
                const { campaign, batch } = getReportContext();
                return (
                  <TabPanel key={tabName} value={value} index={visibleIndex}>
                    <DocumentsTab
                      campaignId={
                        campaign?.campaignId || campaign?.campaignID || null
                      }
                      batchNumber={batch?.batchNumber || null}
                      columnConfig={columnConfigByTab[normalizeTabName(tabName)] || null}
                      refreshToken={documentsRefreshToken}
                      onSectionsLoaded={setDocumentsSections}
                    />
                  </TabPanel>
                );
              }

              return (
                <TabPanel key={tabName} value={value} index={visibleIndex}>
                  {isLoading ? (
                    <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" style={{ padding: '40px', gap: '16px' }}>
                      <Box 
                        style={{
                          display: 'flex',
                          gap: '8px',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        {[0, 1, 2].map((i) => (
                          <Box
                            key={i}
                            style={{
                              width: '12px',
                              height: '12px',
                              borderRadius: '50%',
                              backgroundColor: '#1976d2',
                              animation: `pulse 1.4s ease-in-out ${i * 0.16}s infinite`,
                            }}
                          />
                        ))}
                      </Box>
                      <style>
                        {`
                          @keyframes pulse {
                            0%, 80%, 100% {
                              transform: scale(0.8);
                              opacity: 0.5;
                            }
                            40% {
                              transform: scale(1.2);
                              opacity: 1;
                            }
                          }
                        `}
                      </style>
                      <Typography variant="body2" style={{ color: '#666', marginTop: '8px' }}>
                        Loading {tabName} data...
                      </Typography>
                    </Box>
                  ) : tabData.rows && tabData.rows.length > 0 ? (
                    <GenericTable
                      data={tabData.rows}
                      columns={getVisibleColumns(tabName, tabData.columns)}
                      viewType={viewType}
                      actions={tabActions}
                      featuresAccess={featuresAccess}
                      onActionClick={onActionClick}
                      showActionsColumn={showActionsColumn}
                      pageScrollMaxHeight={pageScrollMaxHeight}
                      onDocLinkClick={handleAnalyticalReportClick}
                      isDocLinkEnabled={isAnalyticalReportAvailable}
                    />
                  ) : getEmptyMessage() ? (
                    <Box style={{ padding: '40px', textAlign: 'center' }}>
                      <Typography variant="body2" style={{ color: hasError ? '#d32f2f' : '#666' }}>
                        {getEmptyMessage()}
                      </Typography>
                    </Box>
                  ) : null}
                </TabPanel>
              );
            })}
          </Box>
        )}
      </Box>
      {showChatBox && data && (
        <ChatBox batchNumber={data.cmoBatchNumber || '400682, 400683, 400684'} />
      )}
      {/* "Analytical Report" link target — a standalone Document Repository
          instance, independent of the one on the main dashboard (ActionButtons),
          deep-linked straight to the matching document instead of the normal
          select-filters-then-Save-view flow. */}
      <DocumentRepository
        open={!!analyticalReportLink}
        repositoryKey="analytical"
        onClose={() => setAnalyticalReportLink(null)}
        recentFilters={null}
        onFiltersChange={() => {}}
        initialCampaignId={analyticalReportLink?.campaignId}
        initialBatchNumber={analyticalReportLink?.batchNumber}
        initialSearch={analyticalReportLink?.search}
      />
    </div>
  );
}

